import { PDFDocument, rgb, type PDFFont } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import type { FacilityMeta } from './tier2submit-export.ts';

export type ReportCertificationDetails = Pick<
  FacilityMeta,
  'name' | 'address' | 'city' | 'state' | 'zip' | 'reportingYear' | 'certifierName' | 'certifierTitle'
>;

// Federal certification language, 40 CFR 370.42(a), reporting year 2025:
// https://www.govinfo.gov/content/pkg/CFR-2025-title40-vol30/pdf/CFR-2025-title40-vol30-sec370-42.pdf
export const TIER_II_CERTIFICATION_STATEMENT =
  'I certify under penalty of law that I have personally examined and am familiar with the information and that based on my inquiry of those individuals responsible for obtaining the information, I believe that the submitted information is true, accurate, and complete.';

/** Creates an unsigned certification. Downloading it never certifies or attaches the report. */
export async function reportCertificationPdfCreate(
  details: ReportCertificationDetails,
  fonts: { regular: Uint8Array; bold: Uint8Array },
): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.registerFontkit(fontkit);
  const [regular, bold] = await Promise.all([
    pdf.embedFont(fonts.regular, { subset: true }),
    pdf.embedFont(fonts.bold, { subset: true }),
  ]);
  pdf.setTitle(`Tier II certification - ${details.name} - ${details.reportingYear}`);
  pdf.setSubject('Unsigned Tier II report certification for review and signature');
  const margin = 54;
  const width = 504;
  const ink = rgb(0.08, 0.12, 0.2);
  const muted = rgb(0.32, 0.38, 0.46);
  let page = pdf.addPage([612, 792]);
  let y = 738;
  const room = (height: number) => {
    if (y - height < 70) {
      page = pdf.addPage([612, 792]);
      y = 738;
    }
  };
  const lines = (value: string, font: PDFFont, size: number) => {
    const result: string[] = [];
    let line = '';
    for (const word of value.trim().split(/\s+/)) {
      const candidate = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) <= width) {
        line = candidate;
        continue;
      }
      if (line) result.push(line);
      line = '';
      // Unbroken facility names/identifiers must also stay inside the print margins.
      for (const character of word) {
        if (font.widthOfTextAtSize(line + character, size) > width) {
          result.push(line);
          line = '';
        }
        line += character;
      }
    }
    if (line) result.push(line);
    return result;
  };
  const text = (value: string, font: PDFFont, size: number, color = ink) => {
    for (const line of lines(value, font, size)) {
      room(size * 1.5);
      page.drawText(line, { x: margin, y, font, size, color });
      y -= size * 1.5;
    }
  };
  const field = (label: string, value: string) => {
    room(55);
    text(label, regular, 9, muted);
    if (value.trim()) text(value, bold, 12);
    else {
      page.drawLine({
        start: { x: margin, y: y - 9 },
        end: { x: margin + width, y: y - 9 },
        color: muted,
        thickness: 0.6,
      });
      y -= 20;
    }
    y -= 12;
  };

  text('Tier II report certification', bold, 23);
  text(`Reporting year ${details.reportingYear} | ${details.state}`, regular, 11, muted);
  y -= 22;
  field('FACILITY NAME', details.name);
  field('FACILITY STREET ADDRESS', details.address);
  field('CITY / STATE / ZIP', [details.city, details.state, details.zip].filter(Boolean).join(', '));

  const statementLines = lines(TIER_II_CERTIFICATION_STATEMENT, regular, 11);
  room(statementLines.length * 16.5 + 175);
  text('Certification', bold, 14);
  y -= 8;
  text(TIER_II_CERTIFICATION_STATEMENT, regular, 11);
  y -= 22;
  field('CERTIFIER FULL NAME', details.certifierName);
  field('OFFICIAL TITLE', details.certifierTitle);
  room(140);
  y -= 20;
  page.drawLine({ start: { x: margin, y }, end: { x: 365, y }, color: muted, thickness: 0.7 });
  page.drawLine({ start: { x: 395, y }, end: { x: 558, y }, color: muted, thickness: 0.7 });
  page.drawText('Signature', { x: margin, y: y - 15, font: regular, size: 9, color: muted });
  page.drawText('Date signed', { x: 395, y: y - 15, font: regular, size: 9, color: muted });
  y -= 48;
  text('Total pages in submission (including attachments): __________', regular, 10);
  y -= 16;
  text(
    'Review the complete report before signing. Upload the signed certification PDF with this report.',
    regular,
    9,
    muted,
  );

  const pages = pdf.getPages();
  pages.forEach((sheet, index) => {
    sheet.drawText(`Tier II | ${details.reportingYear} | ${details.state}`, {
      x: margin,
      y: 32,
      font: regular,
      size: 8,
      color: muted,
    });
    sheet.drawText(`Certification page ${index + 1} of ${pages.length}`, {
      x: 425,
      y: 32,
      font: regular,
      size: 8,
      color: muted,
    });
  });
  return pdf.save();
}
